import { Injectable } from '@angular/core';
import { ANALYTICS_EVENTS } from '../../../constants/utils/analytics.constant';
import {
  AdobeDataLayer,
  ClickEventAnalytics,
  CustomEventErrorAnalytics,
  CustomEventSuccessAnalytics,
  ModalLoadLoginAnalytics,
  PageLoadAnalytics,
  PageLoadErrorAnalytics,
  PageLoadLoginAnalytics
} from '../../../models/utils/analytics.interfaces';
import { triggerCustomEvent } from '../events/events.utils';
import { LoggingService } from '../logging/logging.service';
import { LoginStatusAnalyticsHelper } from './login-status-analytics.helper';

declare const window: {
  adobeDataLayer: AdobeDataLayer[];
} & Window;

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  constructor(private logging: LoggingService) {}
  private isLoggedInGlobal = false;
  /** dispatchCustomEvent as a generic method that passes eventName
   * and detail to dispatch the custom event
   */
  dispatchCustomEvent<T>(eventName: string, detail: T) {
    triggerCustomEvent<T>(eventName, detail);
  }

  /**
   * Update the data layer object used by tealeaf | omniture | analytics
   */
  updateDataLayer<T>(
    property: string,
    data: T | string | undefined,
    path: string
  ) {
    try {
      const properties = path.split('.');
      const dataObject = properties.reduce(
        (prev: Record<string, unknown>, curr: string) =>
          prev && typeof prev === 'object'
            ? (prev[curr] as Record<string, unknown>)
            : ({} as Record<string, unknown>),
        window as unknown as Record<string, unknown>
      );
      if (dataObject && typeof dataObject === 'object') {
        dataObject[property] = data;
      }
    } catch (e) {
      this.logging.log('The data layer object is undefined');
    }
  }

  updateAdobeDataLayerAnalytics(data: AdobeDataLayer): void {
    try {
      window.adobeDataLayer = window.adobeDataLayer || [];
      window.adobeDataLayer.push(data);
    } catch (error) {
      this.logging.log(
        `Error setting Adobe Analytics tracking data: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }

  setAnalytics(
    analytics: PageLoadAnalytics | ClickEventAnalytics | PageLoadErrorAnalytics
  ): void {
    const options = {
      [ANALYTICS_EVENTS.PAGE_LOAD]: () => {
        if ((analytics as PageLoadErrorAnalytics).error == undefined) {
          const pageName = (analytics as PageLoadAnalytics).page.info.page_name;
          (
            analytics as PageLoadAnalytics
          ).page.info.page_name = `dal:${pageName}`;
        }
      },
      [ANALYTICS_EVENTS.CLICK]: () => {
        const pageName = (analytics as ClickEventAnalytics).click.page_section;
        (analytics as ClickEventAnalytics).click.page_section = `${pageName}`;
      },
      [ANALYTICS_EVENTS.ERROR]: () => {
        if ((analytics as PageLoadErrorAnalytics).error.type !== '') {
          const pageName = (analytics as PageLoadErrorAnalytics).page.info
            .page_name;
          (
            analytics as PageLoadErrorAnalytics
          ).page.info.page_name = `dal:${pageName}`;
        }
      }
    };

    const action =
      analytics.event === ANALYTICS_EVENTS.PAGE_LOAD
        ? ANALYTICS_EVENTS.PAGE_LOAD
        : analytics.event === ANALYTICS_EVENTS.CLICK
        ? ANALYTICS_EVENTS.CLICK
        : ANALYTICS_EVENTS.ERROR;

    options[action]();
    this.updateAdobeDataLayerAnalytics(analytics);
  }

  setLoginAnalytics(
    analytics: PageLoadErrorAnalytics | PageLoadLoginAnalytics
  ): void {
    const processedAnalytics =
      LoginStatusAnalyticsHelper.processLoginAnalytics(analytics);
    this.updateAdobeDataLayerAnalytics(processedAnalytics);
  }

  setModalAnalytics(analytics: ModalLoadLoginAnalytics): void {
    this.updateAdobeDataLayerAnalytics(analytics);
  }

  setErrorAnalytics(analytics: CustomEventErrorAnalytics): void {
    this.updateAdobeDataLayerAnalytics(analytics);
  }

  setSuccessAnalytics(analytics: CustomEventSuccessAnalytics): void {
    this.updateAdobeDataLayerAnalytics(analytics);
  }

  setLoginGlobalStatus(loggedInStatus: boolean, errorCode?: string): void {
    this.isLoggedInGlobal = loggedInStatus;
    const data = LoginStatusAnalyticsHelper.createLoginStatusAnalytics(
      loggedInStatus,
      errorCode
    );
    this.setAnalytics(data);
  }

  setIsLoggedIn(value: boolean): void {
    this.isLoggedInGlobal = value;
  }

  getIsLoggedIn(): boolean {
    return this.isLoggedInGlobal;
  }
}
